import React, { useState, useRef, useEffect } from 'react';
import styled from 'styled-components';

const InputContainer = styled.div`
  padding: 1rem;
  background: white;
  border-top: 1px solid #e1e5e9;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const InputRow = styled.div`
  display: flex;
  gap: 0.5rem;
  align-items: flex-end;
`;

const TextAreaContainer = styled.div`
  flex: 1;
  position: relative;
`;

const TextArea = styled.textarea<{ disabled?: boolean }>`
  width: 100%;
  min-height: 40px;
  max-height: 120px;
  padding: 0.75rem;
  border: 2px solid #e1e5e9;
  border-radius: 20px;
  font-size: 0.9rem;
  font-family: inherit;
  resize: none;
  outline: none;
  background: ${props => props.disabled ? '#f5f5f5' : 'white'};
  color: ${props => props.disabled ? '#999' : '#333'};
  transition: border-color 0.2s ease;

  &:focus {
    border-color: #667eea;
  }

  &::placeholder {
    color: #999;
  }

  &:disabled {
    cursor: not-allowed;
  }
`;

const SendButton = styled.button<{ disabled?: boolean }>`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  border: none;
  background: ${props => props.disabled ? '#ccc' : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'};
  color: white;
  cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};
  display: flex;
  align-items: center;
  justify-content: center;
  transition: transform 0.2s ease, box-shadow 0.2s ease;
  flex-shrink: 0;

  &:hover:not(:disabled) {
    transform: scale(1.05);
    box-shadow: 0 4px 12px rgba(102, 126, 234, 0.3);
  }

  &:active:not(:disabled) {
    transform: scale(0.95);
  }

  svg {
    width: 16px;
    height: 16px;
  }
`;

const ControlsRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0 0.5rem;
`;

const AIToggle = styled.button<{ active: boolean }>`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 0.75rem;
  border: 1px solid ${props => props.active ? '#667eea' : '#e1e5e9'};
  border-radius: 20px;
  background: ${props => props.active ? '#667eea' : 'white'};
  color: ${props => props.active ? 'white' : '#666'};
  cursor: pointer;
  font-size: 0.8rem;
  font-weight: 500;
  transition: all 0.2s ease;

  &:hover {
    border-color: #667eea;
    background: ${props => props.active ? '#5a67d8' : '#f8f9ff'};
  }

  svg {
    width: 14px;
    height: 14px;
  }
`;

const CharCount = styled.span<{ isNearLimit: boolean }>`
  font-size: 0.75rem;
  color: ${props => props.isNearLimit ? '#ff6b6b' : '#999'};
`;

const EmojiButton = styled.button`
  width: 32px;
  height: 32px;
  border: none;
  background: none;
  cursor: pointer;
  border-radius: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background-color 0.2s ease;

  &:hover {
    background: #f0f0f0;
  }
`;

const MAX_LENGTH = 1000;

interface MessageInputProps {
  onSendMessage: (content: string, isAI?: boolean) => void;
  onTyping: (isTyping: boolean) => void;
  disabled?: boolean;
  placeholder?: string;
}

const MessageInput: React.FC<MessageInputProps> = ({
  onSendMessage,
  onTyping,
  disabled = false,
  placeholder = "Type a message..."
}) => {
  const [message, setMessage] = useState('');
  const [isAIMode, setIsAIMode] = useState(false);
  const [isTyping, setIsTyping] = useState(false);
  const textAreaRef = useRef<HTMLTextAreaElement>(null);
  const typingTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Auto-resize textarea
  useEffect(() => {
    const textArea = textAreaRef.current;
    if (textArea) {
      textArea.style.height = 'auto';
      textArea.style.height = `${textArea.scrollHeight}px`;
    }
  }, [message]);

  // Handle typing indicator
  useEffect(() => {
    if (message.trim() && !isTyping) {
      setIsTyping(true);
      onTyping(true);
    }

    // Clear typing timeout and set a new one
    if (typingTimeoutRef.current) {
      clearTimeout(typingTimeoutRef.current);
    }

    typingTimeoutRef.current = setTimeout(() => {
      if (isTyping) {
        setIsTyping(false);
        onTyping(false);
      }
    }, 1000);

    return () => {
      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current);
      }
    };
  }, [message, isTyping, onTyping]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    sendMessage();
  };

  const sendMessage = () => {
    if (!message.trim() || disabled) return;

    onSendMessage(message.trim(), isAIMode);
    setMessage('');
    
    // Stop typing indicator
    setIsTyping(false);
    onTyping(false);
    
    // Focus back to input
    textAreaRef.current?.focus();
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const value = e.target.value;
    if (value.length <= MAX_LENGTH) {
      setMessage(value);
    }
  };

  const insertEmoji = (emoji: string) => {
    const newMessage = message + emoji;
    if (newMessage.length <= MAX_LENGTH) {
      setMessage(newMessage);
      textAreaRef.current?.focus();
    }
  };

  const isNearLimit = message.length > MAX_LENGTH * 0.8;
  const canSend = message.trim().length > 0 && !disabled;

  return (
    <InputContainer>
      <ControlsRow>
        <AIToggle 
          active={isAIMode} 
          onClick={() => setIsAIMode(!isAIMode)}
          type="button"
        >
          <svg viewBox="0 0 24 24" fill="currentColor">
            <path d="M12 2L13.09 8.26L16 7L14.74 13.09L21 12L14.74 10.91L16 17L13.09 15.74L12 22L10.91 15.74L8 17L9.26 10.91L3 12L9.26 13.09L8 7L10.91 8.26L12 2Z"/>
          </svg>
          AI Mode
        </AIToggle>
        
        <CharCount isNearLimit={isNearLimit}>
          {message.length}/{MAX_LENGTH}
        </CharCount>
      </ControlsRow>

      <form onSubmit={handleSubmit}>
        <InputRow>
          <TextAreaContainer>
            <TextArea
              ref={textAreaRef}
              value={message}
              onChange={handleChange}
              onKeyDown={handleKeyDown}
              placeholder={isAIMode ? "Ask AI anything..." : placeholder}
              disabled={disabled}
              rows={1}
            />
          </TextAreaContainer>

          <EmojiButton
            type="button"
            onClick={() => insertEmoji('😊')}
            disabled={disabled}
          >
            😊
          </EmojiButton>

          <SendButton
            type="submit"
            disabled={!canSend}
          >
            <svg viewBox="0 0 24 24" fill="currentColor">
              <path d="M2,21L23,12L2,3V10L17,12L2,14V21Z"/>
            </svg>
          </SendButton>
        </InputRow>
      </form>
    </InputContainer>
  );
};

export default MessageInput; 